import React, { useMemo } from "react";
import { cn } from "../lib/utils";
import {
  Check,
  ChevronDown,
  CircleUserRound,
  Loader2,
  LogOut,
} from "lucide-react";
import businessSuiteIcon from "../assets/images/businessSuite/account.svg";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import type { BusinessProfileSummary } from "../services/businessProfileService";

interface SwitchableAccount {
  _id?: string;
  accountId?: string;
  accountName?: string;
  isMainAccount?: boolean;
}

interface HeaderAccountSwitcherProps {
  accounts: SwitchableAccount[];
  accountsError?: boolean;
  activeAccountId?: string | null;
  activeBusinessId: string | null;
  businesses: BusinessProfileSummary[];
  isAccountLoading?: boolean;
  isAccountSwitching?: boolean;
  isBusinessLoading?: boolean;
  isBusinessSwitching?: boolean;
  onLogout: () => void;
  onSelectBusiness: (businessId: string) => void;
  onSelectPersonal: () => void;
  onSelectSubAccount: (account: SwitchableAccount) => void;
  subAccountsEnabled?: boolean;
  compact?: boolean;
}

interface TriggerState {
  description: string;
  title: string;
}

interface ContextOptionProps {
  description: string;
  icon: React.ReactNode;
  isSelected: boolean;
  title: string;
}

interface AccountAvatarProps {
  account: SwitchableAccount;
  className?: string;
}

interface BusinessAvatarProps {
  business: BusinessProfileSummary;
  className?: string;
  imgAlt?: string;
}

interface AccountWalletIconProps {
  className?: string;
  strokeWidth?: number;
}

const getAccountIdentifier = (account: SwitchableAccount) =>
  account._id || account.accountId || "";

function isSameAccount(
  account: SwitchableAccount | null | undefined,
  activeAccountId: string | null | undefined
) {
  if (!account || !activeAccountId) {
    return false;
  }

  return getAccountIdentifier(account) === activeAccountId;
}

function ContextOption({
  description,
  icon,
  isSelected,
  title,
}: ContextOptionProps) {
  return (
    <div className="flex w-full items-center gap-3">
      {icon}
      <div className="min-w-0 flex-1">
        <p className="truncate text-[16px] font-semibold text-[#33125F]">
          {title}
        </p>
        <p className="truncate text-[14px] text-[#7C748F]">{description}</p>
      </div>
      {isSelected ? (
        <Check className="h-5 w-5 shrink-0 text-[#57249C]" aria-hidden />
      ) : null}
    </div>
  );
}

function AccountWalletIcon({
  className = "",
  strokeWidth = 1.7,
}: AccountWalletIconProps) {
  return (
    <svg
      aria-hidden
      className={className}
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={strokeWidth}
      viewBox="0 0 24 24"
    >
      <path d="M17 14h.01" />
      <path d="M7 7h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14" />
    </svg>
  );
}

function AccountAvatar({ account, className }: AccountAvatarProps) {
  if (account.isMainAccount) {
    return (
      <span
        className={cn(
          "flex h-12 w-12 shrink-0 items-center justify-center text-[#57249C]",
          className
        )}
      >
        <AccountWalletIcon className="!h-7 !w-7" />
      </span>
    );
  }

  return (
    <span
      className={cn(
        "flex h-11 w-11 shrink-0 items-center justify-center text-[#57249C]",
        className
      )}
    >
      <AccountWalletIcon className="!h-7 !w-7" />
    </span>
  );
}

function BusinessAvatar({ business, className, imgAlt }: BusinessAvatarProps) {
  const imageSource =
    business.logoUrl ||
    business.avatarUrl ||
    business.logo ||
    business.imageUrl ||
    businessSuiteIcon;

  const resolvedAlt = imgAlt !== undefined ? imgAlt : business.businessName;

  return (
    <img
      src={imageSource}
      alt={resolvedAlt}
      className={cn("h-11 w-11 shrink-0 object-contain", className)}
    />
  );
}

interface AccountSwitcherTriggerContentProps {
  activeAccount: SwitchableAccount | null;
  activeBusiness: BusinessProfileSummary | null;
  compact: boolean;
  isBusy: boolean;
  triggerState: TriggerState;
}

function AccountSwitcherTriggerContent({
  activeAccount,
  activeBusiness,
  compact,
  isBusy,
  triggerState,
}: AccountSwitcherTriggerContentProps) {
  const avatarSlot = activeBusiness ? (
    compact ? (
      <span className="flex h-8 w-8 shrink-0 overflow-hidden rounded-full sm:h-10 sm:w-10">
        <BusinessAvatar
          business={activeBusiness}
          className="h-full w-full object-cover"
          imgAlt=""
        />
      </span>
    ) : (
      <BusinessAvatar business={activeBusiness} imgAlt="" />
    )
  ) : activeAccount ? (
    compact ? (
      <AccountAvatar
        account={activeAccount}
        className="h-8 w-8 sm:h-10 sm:w-10"
      />
    ) : (
      <AccountAvatar account={activeAccount} />
    )
  ) : compact ? (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center text-[#57249C] sm:h-10 sm:w-10">
      <AccountWalletIcon className="!h-5 !w-5 sm:!h-7 sm:!w-7" />
    </span>
  ) : (
    <span className="flex h-12 w-12 shrink-0 items-center justify-center text-[#57249C]">
      <AccountWalletIcon className="!h-7 !w-7" />
    </span>
  );

  const labels = compact ? (
    <span className="min-w-0 flex-1">
      <span className="block truncate text-[12px] font-semibold leading-tight text-[#33125F] sm:text-[15px]">
        {triggerState.title}
      </span>
      <span className="block truncate text-[10px] leading-tight text-[#7C748F] sm:text-[13px]">
        {triggerState.description}
      </span>
    </span>
  ) : (
    <span className="min-w-0">
      <span className="block truncate text-[15px] font-semibold text-[#33125F]">
        {triggerState.title}
      </span>
      <span className="block truncate text-[13px] text-[#7C748F]">
        {triggerState.description}
      </span>
    </span>
  );

  const trailingIcon = isBusy ? (
    <Loader2
      className={cn(
        "shrink-0 animate-spin text-[#6D6584]",
        compact ? "h-3.5 w-3.5 sm:h-4 sm:w-4" : "h-4 w-4"
      )}
    />
  ) : (
    <ChevronDown
      className={cn(
        "shrink-0 text-[#6D6584]",
        compact ? "h-4 w-4 sm:h-5 sm:w-5" : "h-5 w-5"
      )}
      aria-hidden
    />
  );

  return (
    <>
      <span
        className={cn(
          "flex min-w-0 items-center",
          compact ? "flex-1 gap-1.5 sm:gap-2" : "gap-2.5"
        )}
      >
        {avatarSlot}
        {labels}
      </span>
      {trailingIcon}
    </>
  );
}

export default function HeaderAccountSwitcher({
  accounts,
  accountsError = false,
  activeAccountId = null,
  activeBusinessId,
  businesses,
  isAccountLoading = false,
  isAccountSwitching = false,
  isBusinessLoading = false,
  isBusinessSwitching = false,
  onLogout,
  onSelectBusiness,
  onSelectPersonal,
  onSelectSubAccount,
  subAccountsEnabled = false,
  compact = false,
}: HeaderAccountSwitcherProps) {
  const mainAccount = useMemo(
    () => accounts.find((account) => account.isMainAccount) || null,
    [accounts]
  );

  const activeBusiness =
    businesses.find((business) => business.businessId === activeBusinessId) ||
    null;

  const activeAccount =
    accounts.find((account) => isSameAccount(account, activeAccountId)) ||
    mainAccount;

  const visibleSubAccounts = useMemo(() => {
    if (!subAccountsEnabled) {
      return [];
    }

    return accounts.filter((account) => !account.isMainAccount);
  }, [accounts, subAccountsEnabled]);

  const isBusy =
    isAccountLoading ||
    isAccountSwitching ||
    isBusinessLoading ||
    isBusinessSwitching;

  const triggerState: TriggerState = activeBusiness
    ? {
        title: activeBusiness.businessName,
        description: activeBusiness.role?.name || "Business account",
      }
    : {
        title: activeAccount?.accountName || "Main Account",
        description: activeAccount?.isMainAccount
          ? "Personal account"
          : "Sub account",
      };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          aria-label="Switch wallet or account"
          className={cn(
            "items-center justify-between rounded-full bg-white text-left shadow-none hover:bg-white",
            compact
              ? "flex h-11 min-h-11 min-w-0 max-w-[min(40vw,188px)] shrink gap-1.5 border border-[#E8E0F1] pl-1.5 pr-2 sm:h-14 sm:min-h-14 sm:max-w-[min(46vw,280px)] sm:gap-2 sm:pl-2 sm:pr-3"
              : "h-14 max-w-[286px] gap-3 border-[#E8E0F1] pl-3 pr-4"
          )}
        >
          <AccountSwitcherTriggerContent
            activeAccount={activeAccount}
            activeBusiness={activeBusiness}
            compact={compact}
            isBusy={isBusy}
            triggerState={triggerState}
          />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={10}
        className="w-[320px] max-h-[min(520px,calc(100dvh-120px))] min-h-0 overflow-y-auto rounded-xl border border-[#EBE9EF] bg-white p-2 shadow-[0_18px_45px_rgba(51,18,95,0.12)]"
      >
        <DropdownMenuLabel className="px-4 pt-3 text-[12px] font-semibold uppercase tracking-[0.16em] text-[#A39CB4]">
          Personal
        </DropdownMenuLabel>

        <DropdownMenuItem
          className="rounded-xl px-4 py-3"
          onSelect={onSelectPersonal}
          disabled={isBusy}
        >
          <ContextOption
            description="Personal account"
            icon={
              mainAccount ? (
                <AccountAvatar account={mainAccount} />
              ) : (
                <span className="flex h-12 w-12 shrink-0 items-center justify-center text-[#57249C]">
                  <AccountWalletIcon className="!h-7 !w-7" />
                </span>
              )
            }
            isSelected={
              activeBusinessId === null &&
              (mainAccount
                ? isSameAccount(mainAccount, activeAccountId)
                : !activeBusiness)
            }
            title={mainAccount?.accountName || "Main Account"}
          />
        </DropdownMenuItem>

        {subAccountsEnabled &&
          visibleSubAccounts.map((account) => (
            <DropdownMenuItem
              key={getAccountIdentifier(account)}
              className="rounded-xl px-4 py-3"
              onSelect={() => onSelectSubAccount(account)}
              disabled={isBusy}
            >
              <ContextOption
                description="Sub account"
                icon={<AccountAvatar account={account} />}
                isSelected={
                  activeBusinessId === null &&
                  isSameAccount(account, activeAccountId)
                }
                title={account.accountName || "Sub Account"}
              />
            </DropdownMenuItem>
          ))}

        {accountsError && subAccountsEnabled ? (
          <DropdownMenuItem
            disabled
            className="rounded-xl px-4 py-3 text-[#7C748F]"
          >
            <CircleUserRound className="h-4 w-4" aria-hidden />
            Could not load sub accounts
          </DropdownMenuItem>
        ) : null}

        {businesses.length > 0 ? (
          <>
            <DropdownMenuSeparator className="my-2 bg-[#F0EBF5]" />
            <DropdownMenuLabel className="px-4 pt-1 text-[12px] font-semibold uppercase tracking-[0.16em] text-[#A39CB4]">
              Business
            </DropdownMenuLabel>

            {businesses.map((business) => (
              <DropdownMenuItem
                key={business.businessId}
                className="rounded-xl px-4 py-3"
                onSelect={() => onSelectBusiness(business.businessId)}
                disabled={isBusy}
              >
                <ContextOption
                  description={business.role?.name || "Business account"}
                  icon={<BusinessAvatar business={business} />}
                  isSelected={activeBusinessId === business.businessId}
                  title={business.businessName}
                />
              </DropdownMenuItem>
            ))}
          </>
        ) : null}

        <DropdownMenuSeparator className="my-2 bg-[#F0EBF5]" />

        <DropdownMenuItem className="rounded-xl px-4 py-3" onSelect={onLogout}>
          <LogOut className="h-4 w-4 text-[#6D6584]" aria-hidden />
          <span className="text-[15px] font-medium text-[#33125F]">Logout</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
